import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ActiveField } from 'types/components/ActiveField';
import { SortableItem } from './SorteableItem';
import { useTranslation } from 'react-i18next';

interface FieldOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeFields: ActiveField[];
  onSave: (orderedFields: ActiveField[]) => void;
}

export const FieldOrderModal: React.FC<FieldOrderModalProps> = ({ isOpen, onClose, activeFields, onSave }) => {
  const [fields, setFields] = useState<ActiveField[]>([]);
  const { t } = useTranslation();

  // Sincronizar el estado cuando se abre el modal con los campos activos actuales
  useEffect(() => {
    if (isOpen) {
      setFields(activeFields);
    }
  }, [isOpen, activeFields]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFields((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" style={backdropStyle}>
      <div className="modal-content" style={modalStyle}>
        <h3 style={{ marginTop: 0 }}>{t('sort-field-positions')}</h3>
        <p style={{ color: '#666', fontSize: '14px' }}>{t('drag-to-sort')}</p>

        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
            <div style={{ margin: '20px 0' }}>
              {fields.map((field) => (
                <SortableItem key={field.id} id={field.id} label={field.label} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button onClick={onClose} style={btnSecondaryStyle}>
            {t('components.cancel')}
          </button>
          <button onClick={() => onSave(fields)} style={btnPrimaryStyle}>
            {t('components.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Estilos rápidos de ejemplo para el modal (puedes reemplazarlos por tus clases de CSS/Tailwind)
const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: '#white',
  background: '#fff',
  padding: '24px',
  borderRadius: '8px',
  width: '100%',
  maxWidth: '450px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
};

const btnPrimaryStyle: React.CSSProperties = {
  backgroundColor: '#165dff', // Mismo azul de tu botón en la captura
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '4px',
  cursor: 'pointer',
};

const btnSecondaryStyle: React.CSSProperties = {
  backgroundColor: '#f1f1f1',
  color: '#333',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '4px',
  cursor: 'pointer',
};
